/**
 * SVG Normalizer
 *
 * This module provides SVG normalization for dynamic color support.
 * The key insight: normalize SVGs at UPLOAD time, not at render time.
 *
 * How it works:
 * 1. On upload: Convert fills/strokes to `currentColor` (one-time transformation)
 * 2. On render: Just apply color via CSS `color` property (bulletproof)
 * 3. If normalization fails: Display SVG as-is (never broken, just static colors)
 */

/** Result of SVG normalization */
export interface NormalizationResult {
  /** The normalized SVG string */
  svg: string;
  /** Whether normalization was successful */
  normalized: boolean;
  /** Warning or error message if normalization failed */
  message?: string;
}

/** Colors that should not be replaced with currentColor */
const PRESERVE_VALUES = /^(none|transparent|url\(|inherit|currentColor)/i;

/** SVG elements that can have fill/stroke */
const COLORABLE_ELEMENTS = new Set([
  'path',
  'circle',
  'rect',
  'ellipse',
  'polygon',
  'polyline',
  'line',
  'text',
  'tspan',
  'g',
]);

/**
 * Unescape SVG content that was stored in the database
 * This handles common escape sequences from database storage
 */
export function unescapeSvg(svg: string): string {
  if (!svg) return '';

  let cleaned = svg
    // Unescape escape sequences
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    // Remove XML declarations, DOCTYPE, and comments
    .replace(/<\?xml[^>]*\?>/gi, '')
    .replace(/<!DOCTYPE[^>]*>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .trim();

  // Ensure SVG starts with <svg tag
  if (!cleaned.startsWith('<svg')) {
    const svgIndex = cleaned.indexOf('<svg');
    if (svgIndex > 0) {
      cleaned = cleaned.substring(svgIndex);
    }
  }

  return cleaned;
}

/**
 * Normalize an SVG for dynamic color support via CSS `color` property.
 *
 * This function:
 * - Parses the SVG as DOM (reliable, not regex)
 * - Converts all fill/stroke colors to `currentColor`
 * - Removes <style> blocks and inlines their properties
 * - Preserves `fill="none"`, `fill="transparent"`, and `url(#...)` references
 * - Returns original SVG if normalization fails (graceful fallback)
 *
 * @param svgString - The SVG string to normalize
 * @returns Normalization result with the SVG and status
 */
export function normalizeSvgForDynamicColor(svgString: string): NormalizationResult {
  if (!svgString) {
    return { svg: '', normalized: false, message: 'Empty SVG string' };
  }

  try {
    // Clean the SVG first
    const cleanedSvg = unescapeSvg(svgString);

    // Parse as DOM
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleanedSvg, 'image/svg+xml');

    // Check for parse errors
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      return {
        svg: cleanedSvg,
        normalized: false,
        message: 'SVG parse error - displaying with original colors',
      };
    }

    const svgElement = doc.documentElement;
    if (!svgElement || svgElement.tagName.toLowerCase() !== 'svg') {
      return {
        svg: cleanedSvg,
        normalized: false,
        message: 'No valid SVG element found',
      };
    }

    // Step 1: Extract CSS rules from <style> blocks before removing them
    const cssRules = extractCssRules(svgElement);

    // Step 2: Apply CSS rules to elements as inline attributes
    applyCssToElements(svgElement, cssRules);

    // Step 3: Remove all <style> blocks (they cause global CSS conflicts)
    const styleElements = svgElement.querySelectorAll('style');
    styleElements.forEach(el => el.remove());

    // Step 4: Convert all colors to currentColor
    convertColorsToCurrentColor(svgElement);

    // Step 5: Clean up problematic attributes for Safari
    cleanupForSafari(svgElement);

    // Step 6: Optimize viewBox to tightly wrap content (removes whitespace)
    optimizeViewBox(svgElement);

    // Serialize back to string
    const serializer = new XMLSerializer();
    const normalizedSvg = serializer.serializeToString(svgElement);

    return {
      svg: normalizedSvg,
      normalized: true,
    };
  } catch (error) {
    // Graceful fallback - return original SVG
    return {
      svg: unescapeSvg(svgString),
      normalized: false,
      message: `Normalization failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Extract CSS rules from <style> blocks
 */
function extractCssRules(svgElement: Element): Map<string, Record<string, string>> {
  const cssRules = new Map<string, Record<string, string>>();

  const styleElements = svgElement.querySelectorAll('style');
  styleElements.forEach(styleEl => {
    const cssContent = styleEl.textContent || '';

    // Parse CSS rules: .st0{fill:#E3E3E3;} -> { 'st0': { fill: '#E3E3E3' } }
    const rulePattern = /\.([a-zA-Z_][\w-]*)\s*\{([^}]+)\}/g;
    let match;

    while ((match = rulePattern.exec(cssContent)) !== null) {
      const className = match[1];
      const declarations = match[2];
      const rules: Record<string, string> = {};

      declarations.split(';').forEach(decl => {
        const colonIndex = decl.indexOf(':');
        if (colonIndex === -1) return;

        const prop = decl.substring(0, colonIndex).trim();
        const value = decl.substring(colonIndex + 1).trim();

        if (prop && value) {
          rules[prop] = value;
        }
      });

      cssRules.set(className, { ...(cssRules.get(className) || {}), ...rules });
    }
  });

  return cssRules;
}

/**
 * Apply CSS rules to elements as inline attributes
 */
function applyCssToElements(svgElement: Element, cssRules: Map<string, Record<string, string>>) {
  const elementsWithClass = svgElement.querySelectorAll('[class]');

  elementsWithClass.forEach(el => {
    const classAttr = el.getAttribute('class');
    if (!classAttr) return;

    const classNames = classAttr.split(/\s+/).filter(Boolean);

    classNames.forEach(className => {
      const rules = cssRules.get(className);
      if (!rules) return;

      Object.entries(rules).forEach(([prop, value]) => {
        // Map CSS properties to SVG attributes
        const svgAttrProps = [
          'fill',
          'stroke',
          'stroke-width',
          'stroke-linecap',
          'stroke-linejoin',
          'stroke-miterlimit',
          'stroke-dasharray',
          'stroke-dashoffset',
          'stroke-opacity',
          'fill-opacity',
          'opacity',
          'font-size',
          'font-family',
          'font-weight',
          'font-style',
          'text-anchor',
          'dominant-baseline',
          'letter-spacing',
        ];

        if (svgAttrProps.includes(prop)) {
          // Only set if not already present
          if (!el.hasAttribute(prop)) {
            // Clean font-family value (remove quotes)
            const cleanValue = prop === 'font-family' ? value.replace(/^['"]|['"]$/g, '') : value;
            el.setAttribute(prop, cleanValue);
          }
        }
      });
    });

    // Remove class attribute since styles are inlined
    el.removeAttribute('class');
  });
}

/**
 * Convert all fill and stroke colors to currentColor
 */
function convertColorsToCurrentColor(svgElement: Element) {
  const allElements = svgElement.querySelectorAll('*');

  allElements.forEach(el => {
    // Convert fill attribute
    const fillAttr = el.getAttribute('fill');
    if (fillAttr && !PRESERVE_VALUES.test(fillAttr)) {
      el.setAttribute('fill', 'currentColor');
    }

    // Convert stroke attribute
    const strokeAttr = el.getAttribute('stroke');
    if (strokeAttr && !PRESERVE_VALUES.test(strokeAttr)) {
      el.setAttribute('stroke', 'currentColor');
    }

    // Handle inline style attribute
    const styleAttr = el.getAttribute('style');
    if (styleAttr) {
      const newStyle = styleAttr
        .split(';')
        .map(decl => {
          const [prop, value] = decl.split(':').map(s => s?.trim());
          if (!prop || !value) return decl;

          if ((prop === 'fill' || prop === 'stroke') && !PRESERVE_VALUES.test(value)) {
            return `${prop}: currentColor`;
          }
          return decl;
        })
        .join(';');

      el.setAttribute('style', newStyle);
    }
  });

  // Add default fill to shape elements without one
  allElements.forEach(el => {
    const tagName = el.tagName.toLowerCase();

    if (COLORABLE_ELEMENTS.has(tagName) && tagName !== 'g') {
      const hasFill = el.hasAttribute('fill');
      const hasStroke = el.hasAttribute('stroke');

      // If element has no fill and no stroke, add fill
      // Skip if it's a stroke-only element (has stroke but fill="none")
      if (!hasFill && !hasStroke) {
        el.setAttribute('fill', 'currentColor');
      }
    }
  });
}

/**
 * Clean up attributes that cause issues in Safari
 */
function cleanupForSafari(svgElement: Element) {
  // Remove problematic attributes
  svgElement.removeAttribute('xml:space');
  svgElement.removeAttribute('enable-background');

  // Remove percentage-based width/height (Safari flexbox issue)
  const width = svgElement.getAttribute('width');
  const height = svgElement.getAttribute('height');

  if (width?.includes('%')) {
    svgElement.removeAttribute('width');
  }
  if (height?.includes('%')) {
    svgElement.removeAttribute('height');
  }

  // Clean inline style of deprecated properties
  const styleAttr = svgElement.getAttribute('style');
  if (styleAttr) {
    const cleanedStyle = styleAttr
      .replace(/enable-background:[^;]+;?/g, '')
      .replace(/;\s*;/g, ';')
      .trim();

    if (cleanedStyle && cleanedStyle !== ';') {
      svgElement.setAttribute('style', cleanedStyle);
    } else {
      svgElement.removeAttribute('style');
    }
  }

  // Remove xmlns:xlink if not used
  const hasXlinkUsage =
    svgElement.querySelector('[*|href]') !== null || svgElement.innerHTML.includes('xlink:href');

  if (!hasXlinkUsage) {
    svgElement.removeAttribute('xmlns:xlink');
  }
}

/**
 * Optimize the SVG's viewBox to tightly wrap the actual content.
 * This removes excess whitespace/padding that causes icons to appear small.
 *
 * Works by temporarily rendering the SVG to get the actual bounding box,
 * then updating the viewBox to match.
 */
function optimizeViewBox(svgElement: Element): void {
  // Skip if we're not in a browser environment
  if (typeof document === 'undefined') return;

  try {
    // Create a temporary hidden container
    const container = document.createElement('div');
    container.style.cssText =
      'position: absolute; visibility: hidden; width: 0; height: 0; overflow: hidden;';
    document.body.appendChild(container);

    // Clone the SVG element and append to container
    const tempSvg = svgElement.cloneNode(true) as SVGSVGElement;

    // Ensure SVG has dimensions for getBBox to work
    if (!tempSvg.getAttribute('width')) {
      tempSvg.setAttribute('width', '100');
    }
    if (!tempSvg.getAttribute('height')) {
      tempSvg.setAttribute('height', '100');
    }

    container.appendChild(tempSvg);

    // Get the bounding box of all content
    const bbox = tempSvg.getBBox();

    // Clean up
    document.body.removeChild(container);

    // Only update if we got valid bounds with actual content
    if (bbox.width > 0 && bbox.height > 0) {
      // Add small padding (2% on each side)
      const padding = Math.max(bbox.width, bbox.height) * 0.02;
      const newViewBox = `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`;

      svgElement.setAttribute('viewBox', newViewBox);

      // Remove fixed width/height to allow CSS sizing
      // This makes the SVG scale to fill its container
      svgElement.removeAttribute('width');
      svgElement.removeAttribute('height');
    }
  } catch {
    // If getBBox fails (e.g., empty SVG), leave viewBox unchanged
  }
}

/**
 * Check if an SVG is already normalized (uses currentColor)
 */
export function isNormalizedSvg(svgString: string): boolean {
  if (!svgString) return false;
  return svgString.includes('currentColor');
}

/** Result of background detection */
export interface BackgroundDetectionResult {
  hasBackground: boolean;
  backgroundIndex: number;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

/**
 * Detect if SVG has a background element (large shape covering most of viewBox).
 * Backgrounds are typically large rectangles or paths that fill the entire canvas.
 * Returns detection result with confidence level.
 */
export function detectSvgBackground(svgString: string): BackgroundDetectionResult {
  if (!svgString) {
    return { hasBackground: false, backgroundIndex: -1, confidence: 'low', reason: 'Empty SVG' };
  }

  // Skip if not in browser environment
  if (typeof document === 'undefined') {
    return {
      hasBackground: false,
      backgroundIndex: -1,
      confidence: 'low',
      reason: 'Not in browser',
    };
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svgElement = doc.documentElement;

    if (doc.querySelector('parsererror')) {
      return {
        hasBackground: false,
        backgroundIndex: -1,
        confidence: 'low',
        reason: 'Parse error',
      };
    }

    // Get viewBox dimensions
    const viewBox = svgElement.getAttribute('viewBox');
    if (!viewBox) {
      return { hasBackground: false, backgroundIndex: -1, confidence: 'low', reason: 'No viewBox' };
    }

    const vbParts = viewBox.split(/[\s,]+/).map(Number);
    if (vbParts.length < 4) {
      return {
        hasBackground: false,
        backgroundIndex: -1,
        confidence: 'low',
        reason: 'Invalid viewBox',
      };
    }

    const [, , vbWidth, vbHeight] = vbParts;
    const viewBoxArea = vbWidth * vbHeight;

    if (viewBoxArea <= 0) {
      return {
        hasBackground: false,
        backgroundIndex: -1,
        confidence: 'low',
        reason: 'Zero viewBox area',
      };
    }

    // Check first few child elements (backgrounds are usually first)
    const children = Array.from(svgElement.children);
    const shapeElements = ['path', 'rect', 'polygon', 'circle', 'ellipse'];

    for (let i = 0; i < Math.min(children.length, 3); i++) {
      const el = children[i];
      const tagName = el.tagName.toLowerCase();

      // Skip non-shape elements (like style, defs, etc.)
      if (!shapeElements.includes(tagName)) {
        continue;
      }

      // For rect, check if it covers most of viewBox
      if (tagName === 'rect') {
        const x = parseFloat(el.getAttribute('x') || '0');
        const y = parseFloat(el.getAttribute('y') || '0');
        const width = parseFloat(el.getAttribute('width') || '0');
        const height = parseFloat(el.getAttribute('height') || '0');
        const rectArea = width * height;

        // If rect covers >80% of viewBox and starts near origin
        if (rectArea > viewBoxArea * 0.8 && x < vbWidth * 0.1 && y < vbHeight * 0.1) {
          return {
            hasBackground: true,
            backgroundIndex: i,
            confidence: 'high',
            reason: 'Large rectangle covering >80% of viewBox',
          };
        }
      }

      // For path, use getBBox after rendering
      if (tagName === 'path') {
        const tempContainer = document.createElement('div');
        tempContainer.style.cssText =
          'position:absolute;visibility:hidden;width:0;height:0;overflow:hidden;';
        document.body.appendChild(tempContainer);

        try {
          const tempSvg = svgElement.cloneNode(true) as SVGSVGElement;
          tempSvg.setAttribute('width', '100');
          tempSvg.setAttribute('height', '100');
          tempContainer.appendChild(tempSvg);

          const pathEl = tempSvg.children[i] as SVGGraphicsElement;
          if (pathEl && typeof pathEl.getBBox === 'function') {
            const bbox = pathEl.getBBox();
            const pathArea = bbox.width * bbox.height;

            // If path covers >70% of viewBox and is one of the first elements
            if (pathArea > viewBoxArea * 0.7 && i < 2) {
              document.body.removeChild(tempContainer);
              return {
                hasBackground: true,
                backgroundIndex: i,
                confidence: i === 0 ? 'high' : 'medium',
                reason: `Large path covering >${Math.round((pathArea / viewBoxArea) * 100)}% of viewBox`,
              };
            }
          }
        } catch {
          // getBBox failed, skip
        }

        document.body.removeChild(tempContainer);
      }
    }

    return {
      hasBackground: false,
      backgroundIndex: -1,
      confidence: 'high',
      reason: 'No background detected',
    };
  } catch {
    return {
      hasBackground: false,
      backgroundIndex: -1,
      confidence: 'low',
      reason: 'Detection error',
    };
  }
}

/**
 * Remove the detected background element from SVG.
 * Returns the modified SVG string, or original if removal fails.
 */
export function removeSvgBackground(svgString: string): string {
  if (!svgString) return svgString;

  const detection = detectSvgBackground(svgString);

  if (!detection.hasBackground || detection.backgroundIndex < 0) {
    return svgString;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svgElement = doc.documentElement;

    if (doc.querySelector('parsererror')) {
      return svgString;
    }

    // Remove the element at the detected index
    const children = Array.from(svgElement.children);
    if (detection.backgroundIndex < children.length) {
      children[detection.backgroundIndex].remove();
    }

    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgElement);
  } catch {
    return svgString;
  }
}

/**
 * Resize SVG to specific dimensions while ensuring proper scaling via viewBox.
 *
 * This function ensures that the SVG has a viewBox before setting new dimensions.
 * Without a viewBox, content drawn at coordinates larger than the new dimensions
 * will be clipped. With a viewBox, the content scales to fit the new size.
 *
 * IMPORTANT: This function also scales stroke-width values proportionally to maintain
 * visual consistency. Without this, strokes appear thinner when SVGs are scaled up.
 *
 * @param svg - The SVG string to resize
 * @param width - The target width in pixels
 * @param height - The target height in pixels
 * @returns The resized SVG string
 */
export function resizeSvg(svg: string, width: number, height: number): string {
  if (!svg) return '';

  try {
    // Parse the SVG as DOM for reliable manipulation
    const parser = new DOMParser();
    const doc = parser.parseFromString(svg, 'image/svg+xml');
    const svgElement = doc.documentElement;

    // Check for parse errors
    if (svgElement.tagName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) {
      // Fallback to regex if parsing fails
      return resizeSvgFallback(svg, width, height);
    }

    // Get existing viewBox or extract from width/height
    let viewBox = svgElement.getAttribute('viewBox');

    if (!viewBox) {
      // Try to get original dimensions from attributes
      const origWidth = parseFloat(svgElement.getAttribute('width') || '0');
      const origHeight = parseFloat(svgElement.getAttribute('height') || '0');

      if (origWidth > 0 && origHeight > 0) {
        // Create viewBox from original dimensions
        viewBox = `0 0 ${origWidth} ${origHeight}`;
        svgElement.setAttribute('viewBox', viewBox);
      }
      // If no dimensions found, SVG may use intrinsic sizing - leave viewBox as-is
    }

    // Scale stroke-width values proportionally based on size change
    // This is critical for maintaining visual consistency when resizing icons
    if (viewBox) {
      const vbParts = viewBox.split(/[\s,]+/).map(Number);
      if (vbParts.length >= 4) {
        const vbWidth = vbParts[2];
        const vbHeight = vbParts[3];
        if (vbWidth > 0 && vbHeight > 0) {
          const scaleFactor = Math.min(width / vbWidth, height / vbHeight);

          // Scale stroke-width attributes on all elements
          const allElements = svgElement.querySelectorAll('*');
          allElements.forEach(el => {
            const strokeWidth = el.getAttribute('stroke-width');
            if (strokeWidth) {
              const currentWidth = parseFloat(strokeWidth);
              if (!isNaN(currentWidth)) {
                el.setAttribute('stroke-width', String(currentWidth * scaleFactor));
              }
            }
          });
        }
      }
    }

    // Set new dimensions
    svgElement.setAttribute('width', String(width));
    svgElement.setAttribute('height', String(height));

    // Serialize back to string
    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgElement);
  } catch {
    // Fallback to regex-based approach if DOM parsing fails
    return resizeSvgFallback(svg, width, height);
  }
}

/**
 * Regex-based fallback for SVGs that fail DOM parsing.
 * Preserves the original behavior with added viewBox handling.
 */
function resizeSvgFallback(svg: string, width: number, height: number): string {
  let resizedSvg = svg;

  // Check if viewBox exists
  const hasViewBox = /viewBox\s*=/.test(resizedSvg);

  if (!hasViewBox) {
    // Try to extract original dimensions and create viewBox
    const widthMatch = resizedSvg.match(/width\s*=\s*["']?(\d+(?:\.\d+)?)/);
    const heightMatch = resizedSvg.match(/height\s*=\s*["']?(\d+(?:\.\d+)?)/);

    if (widthMatch && heightMatch) {
      const origWidth = widthMatch[1];
      const origHeight = heightMatch[1];
      // Add viewBox before setting new dimensions
      resizedSvg = resizedSvg.replace('<svg', `<svg viewBox="0 0 ${origWidth} ${origHeight}"`);
    }
  }

  // Update width and height attributes
  resizedSvg = resizedSvg.replace(/width="[^"]*"/g, `width="${width}"`);
  resizedSvg = resizedSvg.replace(/height="[^"]*"/g, `height="${height}"`);

  // If no width/height attributes existed, add them
  if (!resizedSvg.includes('width=')) {
    resizedSvg = resizedSvg.replace('<svg', `<svg width="${width}"`);
  }
  if (!resizedSvg.includes('height=')) {
    resizedSvg = resizedSvg.replace('<svg', `<svg height="${height}"`);
  }

  return resizedSvg;
}
